import { motion } from "framer-motion";
import { Anchor, Compass, Wrench, GraduationCap } from "@phosphor-icons/react";
import CareersForm from "../components/forms/CareersForm";
import useSEO from "../lib/useSEO";

const TRACKS = [
    { Icon: Anchor, t: "Sea Crew", d: "Master, officers, engineers and ratings — across bulkers, tankers and OSVs." },
    { Icon: Compass, t: "Marine Engineering", d: "Naval architecture, drydock supervision, newbuild and repair specialists." },
    { Icon: Wrench, t: "Operations & Chartering", d: "Voyage operators, brokers, post-fixture and demurrage analysts." },
    { Icon: GraduationCap, t: "Cadet Programme", d: "Sponsored deck and engine cadetships with on-board training." },
];

const OPENINGS = [
    { role: "Master / Captain", type: "Sea-going", location: "Worldwide" },
    { role: "Chief Engineer", type: "Sea-going", location: "Worldwide" },
    { role: "2nd Officer", type: "Sea-going", location: "Worldwide" },
    { role: "Marine Superintendent", type: "Shore", location: "Singapore" },
    { role: "Charter Broker — Tankers", type: "Shore", location: "Singapore" },
    { role: "Voyage Operations Analyst", type: "Shore", location: "Singapore / Dubai" },
];

export default function Careers() {
    useSEO({
        title: "Careers — Windermere Shipping",
        description: "Join the Windermere crew. Sea-going, shore-based, marine engineering and cadet programme opportunities.",
    });
    return (
        <>
            <section className="relative pt-36 md:pt-44 pb-20 bg-[#040914] border-b border-white/5 overflow-hidden">
                <div className="absolute inset-0 wms-radial pointer-events-none" />
                <div className="wms-container relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <span className="wms-overline">Careers — Crew & Shore</span>
                        <h1 className="mt-6 font-display font-light text-[clamp(2.4rem,6vw,5rem)] leading-[1.05] tracking-tight">
                            Built for sailors. <br /> <span className="italic text-[#48CAE4]">Operated by professionals.</span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-white/60 leading-relaxed">
                        Windermere is a long-tenure employer. We invest in our crews and shore desks for the long haul — competitive packages, MLC welfare and structured career pathing.
                    </p>
                </div>
            </section>

            <section className="py-24 bg-[#040914]">
                <div className="wms-container">
                    <span className="wms-overline">Career Tracks</span>
                    <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight">Pick the deck that fits you.</h2>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
                        {TRACKS.map(({ Icon, t, d }, i) => (
                            <motion.div
                                key={t}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                data-testid={`career-track-${i}`}
                                className="bg-[#040914] p-7 hover:bg-[#0B1324] transition-colors group"
                            >
                                <Icon size={28} weight="duotone" className="text-[#48CAE4]" />
                                <h3 className="mt-6 font-display text-lg">{t}</h3>
                                <p className="mt-3 text-sm text-white/55 leading-relaxed">{d}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 bg-[#0B1324]">
                <div className="wms-container">
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <span className="wms-overline">Current Openings</span>
                            <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight">Roles open now.</h2>
                        </div>
                        <span className="text-[10px] tracking-[0.32em] uppercase text-white/40">Updated weekly</span>
                    </div>
                    <ul className="mt-10 border-t border-white/10" data-testid="career-openings">
                        {OPENINGS.map((o, i) => (
                            <li key={o.role} className="grid grid-cols-12 items-center py-6 border-b border-white/10 hover:bg-[#101a30] transition-colors">
                                <span className="col-span-2 md:col-span-1 font-display text-[#48CAE4] text-sm tracking-[0.32em]">{String(i + 1).padStart(2, "0")}</span>
                                <span className="col-span-10 md:col-span-5 font-display text-lg text-white">{o.role}</span>
                                <span className="hidden md:block md:col-span-3 text-sm text-white/55">{o.type}</span>
                                <span className="hidden md:block md:col-span-2 text-sm text-white/55">{o.location}</span>
                                <span className="col-span-12 md:col-span-1 text-right text-[11px] uppercase tracking-[0.28em] text-white/60 mt-2 md:mt-0">Apply ↓</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="py-24 bg-[#040914]">
                <div className="wms-container grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <span className="wms-overline">Application</span>
                        <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight">Submit your candidature.</h2>
                        <p className="mt-6 text-white/60 leading-relaxed">
                            Our crewing and HR desks review applications weekly. Suitable candidates will be invited to a screening interview.
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        <CareersForm />
                    </div>
                </div>
            </section>
        </>
    );
}
